/**
 * CurrencyBottomSheet — iter334
 *
 * Premium full-screen bottom sheet that replaces the native <select>
 * for currency / dialing-code picking. Rendered above everything else
 * via React portal so it works inside any modal / sticky-nav layout.
 *
 * Props
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (item) => void   item: { code, name_en, name_ar, flag, dial_code?, recommended? }
 *  - items: array of the same shape (already filtered to the country's
 *           primary + USD for the delivery picker, OR the full
 *           country dial-code list for the phone picker)
 *  - title: string (already translated by caller — EN or AR)
 *  - searchPlaceholder: string
 *  - selectedCode: string (e.g. "SDG" or "+249") — receives the checkmark
 *  - mode: 'currency' | 'dial_code' — controls which field maps to the
 *           selection key (code vs dial_code) and which label is primary.
 *  - isRTL: boolean
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Check, Star } from 'lucide-react';
import './CurrencyBottomSheet.css';

export default function CurrencyBottomSheet({
  open,
  onClose,
  onSelect,
  items = [],
  title,
  searchPlaceholder,
  selectedCode,
  mode = 'currency',
  isRTL = false,
}) {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const t = setTimeout(() => searchRef.current?.focus(), 240);
    // Lock body scroll while open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const fields = [
        it.code,
        it.dial_code,
        it.name_en,
        it.name_ar,
        it.country_name_en,
        it.country_name_ar,
      ];
      return fields.some((f) => f && f.toLowerCase().includes(q));
    });
  }, [items, query]);

  if (!open) return null;

  const primaryKey = mode === 'dial_code' ? 'dial_code' : 'code';

  return createPortal(
    <div
      className="iter334-sheet-backdrop"
      onClick={onClose}
      data-testid="currency-sheet-backdrop"
    >
      <div
        className={`iter334-sheet ${isRTL ? 'is-rtl' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="currency-sheet"
      >
        <div className="iter334-sheet-handle" aria-hidden="true" />
        <header className="iter334-sheet-header">
          <h3 className="iter334-sheet-title" data-testid="currency-sheet-title">
            {title}
          </h3>
          <button
            type="button"
            className="iter334-sheet-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="currency-sheet-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="iter334-sheet-search">
          <Search size={16} className="iter334-sheet-search-icon" />
          <input
            ref={searchRef}
            type="text"
            className="iter334-sheet-search-input"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="currency-sheet-search"
          />
        </div>

        <ul className="iter334-sheet-list" role="listbox">
          {filtered.length === 0 && (
            <li className="iter334-sheet-empty" data-testid="currency-sheet-empty">
              {isRTL ? 'لا توجد نتائج' : 'No results'}
            </li>
          )}
          {filtered.map((it) => {
            const k = it[primaryKey];
            const isSelected = String(k) === String(selectedCode);
            return (
              <li
                key={`${k}-${it.code || it.dial_code}`}
                className={`iter334-sheet-row ${isSelected ? 'is-selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onSelect(it);
                  onClose();
                }}
                data-testid={`currency-sheet-row-${k}`}
              >
                <span className="iter334-sheet-row-flag" aria-hidden="true">
                  {it.flag || '🏳️'}
                </span>
                <div className="iter334-sheet-row-text">
                  <div className="iter334-sheet-row-primary">
                    {mode === 'dial_code' ? (
                      <>
                        <span className="iter334-sheet-row-dial">{it.dial_code}</span>
                        <span className="iter334-sheet-row-country">
                          {isRTL ? it.country_name_ar : it.country_name_en}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="iter334-sheet-row-code">{it.code}</span>
                        <span className="iter334-sheet-row-name">
                          {isRTL ? it.name_ar : it.name_en}
                        </span>
                      </>
                    )}
                  </div>
                  {it.recommended && (
                    <span className="iter334-sheet-row-badge" data-testid={`currency-sheet-recommended-${k}`}>
                      <Star size={11} />
                      {isRTL ? 'موصى به' : 'Recommended'}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <Check
                    size={18}
                    className="iter334-sheet-row-check"
                    data-testid={`currency-sheet-check-${k}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body
  );
}
